import React from 'react';
import { Container, Typography, Box, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '30vh',
  minHeight: '250px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ContentSection = styled(Box)(({ theme }) => ({
  py: theme.spacing(6),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const cookiesStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Cookies Policy - Thrana',
  url: 'https://www.thranatech.com/cookies',
  description: 'Cookies Policy for Thrana Softech Pvt. Ltd. Learn how we use cookies and similar technologies on our website.',
};

const Cookies = () => {
  return (
    <>
      <SEO
        title="Cookies Policy | Thrana"
        description="Cookies Policy for Thrana Softech Pvt. Ltd. Learn how we use cookies and similar technologies on our website."
        path="/cookies"
        structuredData={cookiesStructuredData}
      />
      <HeroSection>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            Cookies Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              color: 'white',
            }}
          >
            Effective Date: 20-01-2026
          </Typography>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="lg">
          <Typography variant="body1" style={{marginTop:'20px'}} paragraph sx={{ mb: 4 }}>
            At <strong>Thrana Softech Pvt. Ltd.</strong> ("we," "us," or "our"), we are committed to protecting your privacy and ensuring a safe online experience. This Cookies Policy explains how we use cookies and similar technologies on our website{' '}
            <a href="https://www.thranatech.com" style={{ color: '#3c005a' }}>www.thranatech.com</a> ("the Website") and our pharmacovigilance software products and services ("Services").
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            By using the Website or Services, you consent to the use of cookies in accordance with this policy. If you do not agree with the use of cookies, please adjust your browser settings or refrain from using the Website or Services.
          </Typography>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              1. What are Cookies?
            </Typography>
            <Typography variant="body1" paragraph>
              Cookies are small text files placed on your device (computer, smartphone, tablet, etc.) when you visit a website. They are used to store certain information, such as your preferences or login status, and help improve your experience while using the Website or Services.
            </Typography>
            <Typography variant="body1" paragraph>
              There are different types of cookies, including:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Typography variant="body1">
                  <strong>First-party cookies:</strong> Cookies set by the website you are currently visiting.
                </Typography>
              </li>
              <li>
                <Typography variant="body1">
                  <strong>Third-party cookies:</strong> Cookies set by a domain other than the website you are visiting. Third parties may use these cookies for purposes such as advertising and analytics.
                </Typography>
              </li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              2. Types of Cookies We Use
            </Typography>
            <Typography variant="body1" paragraph>
              We use the following types of cookies on our Website:
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              a. Necessary Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies are essential for the operation of the Website and enable basic functionality such as navigation, access to secure areas, or form submission. Without these cookies, certain parts of the Website may not function properly.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
              Example: Authentication cookies that ensure you remain logged in while accessing secure sections of the Website.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              b. Analytical / Performance Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies help us understand how visitors interact with our Website by collecting and reporting information. They allow us to analyze trends, track user movement, and improve our Services.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
              Example: Google Analytics cookies that collect data on visitor numbers and navigation behavior.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              c. Functional Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies allow the Website to remember choices you make (such as username, language preference, or region) and provide enhanced, personalized features.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
              Example: Remembering a previously entered username for auto-fill during login.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ color: '#3c005a', mt: 3, mb: 1 }}>
              d. Targeting / Advertising Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              These cookies are used to deliver personalized advertisements based on your interests, both on our Website and on third-party websites. They also help limit ad frequency and measure campaign effectiveness.
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
              Example: Facebook or Google AdSense cookies that display relevant ads on other websites you visit.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              3. How We Use Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies for the following purposes:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1"><strong>Enhancing User Experience:</strong> Remembering preferences and settings for a personalized experience.</Typography></li>
              <li><Typography variant="body1"><strong>Improving Website Performance:</strong> Understanding how visitors use the Website to optimize functionality.</Typography></li>
              <li><Typography variant="body1"><strong>Supporting Analytics and Marketing:</strong> Analyzing traffic, measuring performance, and delivering tailored advertisements.</Typography></li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              4. Managing Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              You have control over cookies stored on your device. You can adjust your browser settings to block or delete cookies, or set alerts when cookies are being sent. Please note that disabling cookies may affect Website functionality and user experience.
            </Typography>
            <Typography variant="body1" paragraph>
              You can manage cookies by:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li><Typography variant="body1"><strong>Changing Browser Settings:</strong> Most browsers allow cookie management through privacy or security settings.</Typography></li>
              <li><Typography variant="body1"><strong>Using Cookie Management Tools:</strong> Third-party tools such as <em>Ghostery</em> or <em>Cookiebot</em> can help manage cookies.</Typography></li>
            </Box>
            <Typography variant="body1" paragraph>
              For browser-specific guidance:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Link href="https://support.google.com/chrome/answer/95647?hl=en" target="_blank" rel="noopener noreferrer" sx={{ color: '#3c005a' }}>
                  Google Chrome
                </Link>
              </li>
              <li>
                <Link href="https://support.mozilla.com/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" sx={{ color: '#3c005a' }}>
                  Mozilla Firefox
                </Link>
              </li>
              <li>
                <Link href="https://help.apple.com/safari/mac/8.0/en.lproj/sfri11471.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#3c005a' }}>
                  Safari
                </Link>
              </li>
              <li>
                <Link href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge" target="_blank" rel="noopener noreferrer" sx={{ color: '#3c005a' }}>
                  Microsoft Edge
                </Link>
              </li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              5. Third-Party Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              We may use third-party services that place cookies on your device, including advertising networks, social media platforms, and analytics providers. These cookies may track user behavior, measure ad performance, and deliver personalized content.
            </Typography>
            <Typography variant="body1" paragraph>
              For more details, refer to the privacy policies of respective third parties:
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 2 }}>
              <li>
                <Link href="https://support.google.com/analytics/answer/7318509?hl=en" target="_blank" rel="noopener noreferrer" sx={{ color: '#3c005a' }}>
                  Google Analytics
                </Link>
              </li>
            </Box>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              6. Consent to Use Cookies
            </Typography>
            <Typography variant="body1" paragraph>
              By continuing to use our Website, you consent to the use of cookies as described in this Cookies Policy. If you do not wish to accept cookies, you may manage your preferences or discontinue using the Website.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              7. Changes to This Cookies Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Cookies Policy periodically to reflect operational, legal, or regulatory changes. Updates will be posted on this page, and the <strong>Effective Date</strong> will be revised accordingly.
            </Typography>
          </Section>

          <Section>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a', mt: 4, mb: 2 }}>
              8. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions or concerns regarding this Cookies Policy, please contact us:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" paragraph>
                <strong>Thrana Softech Pvt. Ltd.</strong><br />
                No. 7, Sri Bhavani Arcade, 2nd Floor,<br />
                Anna Swami Mudaliar Rd, Halasuru,<br />
                Bengaluru – 560 042, India<br /><br />
                <strong>Email:</strong> contact@thranatech.com
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
              By using our Website, you agree to this Cookies Policy and consent to the use of cookies as described.
            </Typography>
          </Section>
        </Container>
      </ContentSection>
    </>
  );
};

export default Cookies;
